import React, { Component } from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";

const STATUS_TONE = {
  enrolled: "success",
  dropped: "danger",
  completed: "info"
};

const styles = {
  filterSelect: { padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", minWidth: "220px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  formControl: { width: "100%", marginTop: "8px" },
  statCard: { textAlign: "center", padding: "12px", background: "#f5f5f5", borderRadius: "8px" },
  statNum: { fontSize: "26px", fontWeight: "bold", color: "#1a237e", margin: 0 },
  statLabel: { fontSize: "12px", color: "#666", margin: "4px 0 0" },
  emptyState: { textAlign: "center", color: "#999", padding: "30px" }
};

class PortalEnrollment extends Component {
  state = {
    semesters: [], selectedSemester: "", loadingSemesters: true,
    enrollments: [], loading: false,
    availableCourses: [], pickedCourseId: "",
    enrollOpen: false, saving: false, error: null
  };

  componentDidMount() {
    this.fetchSemesters();
  }

  fetchSemesters = async () => {
    try {
      const res = await api.get("/portal/semesters");
      const list = res.data.data || res.data;
      const current = list.find(s => s.isCurrent) || list[0];
      this.setState(
        { semesters: list, selectedSemester: current ? String(current.id) : "", loadingSemesters: false },
        () => { if (this.state.selectedSemester) this.fetchEnrollments(); }
      );
    } catch (e) {
      this.setState({ loadingSemesters: false });
    }
  };

  fetchEnrollments = async () => {
    const { selectedSemester } = this.state;
    if (!selectedSemester) return;
    this.setState({ loading: true });
    try {
      const res = await api.get("/portal/enrollments", { params: { semesterId: selectedSemester } });
      const d = res.data.data || res.data;
      this.setState({ enrollments: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) {
      this.setState({ enrollments: [], loading: false });
    }
  };

  fetchAvailableCourses = async () => {
    const { selectedSemester } = this.state;
    if (!selectedSemester) return;
    try {
      const res = await api.get("/portal/courses/available", { params: { semesterId: selectedSemester } });
      const d = res.data.data || res.data;
      this.setState({ availableCourses: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { this.setState({ availableCourses: [] }); }
  };

  handleSemesterChange = e => {
    this.setState({ selectedSemester: e.target.value }, this.fetchEnrollments);
  };

  openEnrollDialog = () => {
    this.setState({ enrollOpen: true, pickedCourseId: "", error: null });
    this.fetchAvailableCourses();
  };

  handleEnroll = async () => {
    const { selectedSemester, pickedCourseId } = this.state;
    if (!pickedCourseId) { this.setState({ error: "Select a course first." }); return; }
    this.setState({ saving: true, error: null });
    try {
      await api.post("/portal/enroll", { courseId: pickedCourseId, semesterId: selectedSemester });
      this.setState({ enrollOpen: false, saving: false, pickedCourseId: "" });
      this.fetchEnrollments();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Enrollment failed.", saving: false });
    }
  };

  handleDrop = async id => {
    if (!window.confirm("Drop this course?")) return;
    try {
      await api.patch(`/portal/enrollments/${id}/drop`);
      this.fetchEnrollments();
    } catch (e) { /* silent */ }
  };

  render() {
    const { classes } = this.props;
    const {
      semesters, selectedSemester, loadingSemesters,
      enrollments, loading,
      availableCourses, pickedCourseId, enrollOpen, saving, error
    } = this.state;

    const activeEnrollments = enrollments.filter(e => e.status === "enrolled");
    const totalCredits = activeEnrollments.reduce((sum, e) => sum + (e.course?.creditUnits || 0), 0);

    const columns = [
      { id: "code", header: "Code", accessorFn: e => e.course?.code || "—", meta: { variant: "mono" } },
      { id: "course", header: "Course", accessorFn: e => e.course?.name || "—" },
      { id: "credits", header: "Credits", accessorFn: e => e.course?.creditUnits || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => <Pill tone={STATUS_TONE[info.row.original.status] || "muted"}>{info.row.original.status}</Pill>
      },
      {
        id: "actions",
        header: "",
        cell: info =>
          info.row.original.status === "enrolled" && (
            <Button size="sm" color="danger" simple onClick={() => this.handleDrop(info.row.original.id)}>Drop</Button>
          )
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>My Enrollment</h4>
                <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                  Enroll or drop courses for the selected semester
                </p>
              </div>
              <Button size="sm" color="white" onClick={this.openEnrollDialog} disabled={!selectedSemester}>
                <Icon style={{ marginRight: "4px", fontSize: "16px" }}>add</Icon> Enroll in Course
              </Button>
            </CardHeader>
            <CardBody>
              <div style={{ marginBottom: "20px" }}>
                <label style={{ fontSize: "12px", color: "#666", display: "block", marginBottom: "4px" }}>Semester</label>
                {loadingSemesters ? (
                  <CircularProgress size={20} />
                ) : (
                  <select className={classes.filterSelect} value={selectedSemester} onChange={this.handleSemesterChange}>
                    <option value="">— Select Semester —</option>
                    {semesters.map(s => (
                      <option key={s.id} value={String(s.id)}>{s.name || `Semester ${s.id}`}</option>
                    ))}
                  </select>
                )}
              </div>

              <GridContainer style={{ marginBottom: "16px" }}>
                <GridItem xs={12} sm={4}>
                  <div className={classes.statCard}>
                    <p className={classes.statNum}>{activeEnrollments.length}</p>
                    <p className={classes.statLabel}>Enrolled Courses</p>
                  </div>
                </GridItem>
                <GridItem xs={12} sm={4}>
                  <div className={classes.statCard}>
                    <p className={classes.statNum}>{totalCredits}</p>
                    <p className={classes.statLabel}>Total Credits</p>
                  </div>
                </GridItem>
              </GridContainer>

              <DataTable
                columns={columns}
                data={enrollments}
                isLoading={loading}
                emptyState={
                  selectedSemester ? "No enrollments for this semester yet." : "Select a semester to view enrollments."
                }
              />
            </CardBody>
          </Card>
        </GridItem>

        <Dialog open={enrollOpen} onClose={() => this.setState({ enrollOpen: false })} maxWidth="xs" fullWidth>
          <DialogHeader title="Enroll in Course" />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <FormControl className={classes.formControl}>
              <InputLabel>Course</InputLabel>
              <Select value={pickedCourseId} onChange={e => this.setState({ pickedCourseId: e.target.value })}>
                <MenuItem value="">— Select Course —</MenuItem>
                {availableCourses.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.code} — {c.name} ({c.creditUnits} cr)</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ enrollOpen: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleEnroll} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Enroll"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

PortalEnrollment.propTypes = { classes: PropTypes.object.isRequired };
export default withStyles(styles)(PortalEnrollment);
